import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Boxes, Upload, Settings2, Download, Columns2, Square, Box, ChevronDown, FolderOpen, Undo2, Redo2 } from "lucide-react";
import { useCad } from "../context/CadContext";
import { loadBaseFile } from "../lib/loaders";
import { buildExport } from "../lib/geometry";
import { exportSTL, exportPerObjectSTL, export3MF } from "../lib/exporters";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { SettingsDialog } from "./SettingsDialog";
import { ProjectsDialog } from "./ProjectsDialog";

const LayoutBtn = ({ mode, icon: Icon, label, active, onClick }) => (
  <button
    data-testid={`layout-${mode}-btn`}
    onClick={onClick}
    title={label}
    className={`h-8 w-8 grid place-items-center rounded-md transition-colors duration-150 ${
      active ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
    }`}
  >
    <Icon size={16} />
  </button>
);

export function Header() {
  const { base, setBase, layers, settings, layout, setLayout, undo, redo, canUndo, canRedo } = useCad();
  const fileRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const b = await loadBaseFile(file);
      setBase(b);
      toast.success(`Imported ${b.name}`, {
        description: `${b.size.x.toFixed(1)} × ${b.size.y.toFixed(1)} × ${b.size.z.toFixed(1)} mm`,
      });
    } catch (err) {
      toast.error("Import failed", { description: String(err.message || err) });
    }
  };

  const doExport = async (fmt) => {
    if (!layers.length && !base) {
      toast.error("Nothing to export yet");
      return;
    }
    setBusy(true);
    try {
      const { solids, baseGeoms } = buildExport(layers, settings, base);
      if (fmt === "stl") {
        exportSTL(solids, baseGeoms, "platesnap.stl");
      } else if (fmt === "stl-parts") {
        await exportPerObjectSTL(solids, baseGeoms, "platesnap_parts.zip");
      } else {
        await export3MF(solids, baseGeoms, "platesnap.3mf");
      }
      const label = fmt === "stl-parts" ? "STL parts (ZIP)" : fmt.toUpperCase();
      toast.success(`Exported ${label}`, { description: fmt === "stl-parts" ? "One STL per body" : "Ready for your slicer" });
    } catch (err) {
      toast.error("Export failed", { description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <header className="h-14 shrink-0 flex items-center gap-3 px-4 bg-[#090D16]/90 backdrop-blur-md border-b border-slate-800 z-30">
      <div className="flex items-center gap-2.5">
        <div className="relative h-8 w-8 grid place-items-center rounded-lg bg-emerald-500/15 border border-emerald-500/30">
          <Boxes size={18} className="text-emerald-400" />
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(16,185,129,0.6)]" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-slate-100 text-sm tracking-tight">PlateSnap CAD Lab</div>
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">MakerWorld · MakerLab</div>
        </div>
      </div>

      <div className="flex-1" />

      <input ref={fileRef} type="file" accept=".stl,.3mf" onChange={onImport} className="hidden" data-testid="import-file-input" />
      <Button
        data-testid="import-base-btn"
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
        className="h-9 gap-2 border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-white"
      >
        <Upload size={15} /> Import Base
      </Button>

      <Button
        data-testid="open-projects-btn"
        variant="outline"
        size="sm"
        onClick={() => setProjectsOpen(true)}
        className="h-9 gap-2 border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-white"
      >
        <FolderOpen size={15} /> Projects
      </Button>

      <Button
        data-testid="open-settings-btn"
        variant="outline"
        size="sm"
        onClick={() => setSettingsOpen(true)}
        className="h-9 gap-2 border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-white"
      >
        <Settings2 size={15} /> Settings
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="export-menu-btn"
            size="sm"
            disabled={busy}
            className="h-9 gap-2 bg-emerald-500 text-white hover:bg-emerald-600 font-medium"
          >
            <Download size={15} /> Export <ChevronDown size={13} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 text-slate-200">
          <DropdownMenuItem data-testid="export-stl-btn" onClick={() => doExport("stl")} className="focus:bg-slate-800 focus:text-white cursor-pointer">
            Export STL (single)
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="export-stl-parts-btn" onClick={() => doExport("stl-parts")} className="focus:bg-slate-800 focus:text-white cursor-pointer">
            Export STL parts (ZIP)
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="export-3mf-btn" onClick={() => doExport("3mf")} className="focus:bg-slate-800 focus:text-white cursor-pointer">
            Export 3MF
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex items-center gap-1 ml-1 pl-2 border-l border-slate-800">
        <LayoutBtn mode="split" icon={Columns2} label="Split view" active={layout === "split"} onClick={() => setLayout("split")} />
        <LayoutBtn mode="2d" icon={Square} label="2D only" active={layout === "2d"} onClick={() => setLayout("2d")} />
        <LayoutBtn mode="3d" icon={Box} label="3D only" active={layout === "3d"} onClick={() => setLayout("3d")} />
      </div>

      <div className="flex items-center gap-1 pl-2 border-l border-slate-800">
        <button data-testid="undo-btn" onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)"
          className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:bg-slate-800 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors duration-150"><Undo2 size={16} /></button>
        <button data-testid="redo-btn" onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)"
          className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:bg-slate-800 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors duration-150"><Redo2 size={16} /></button>
      </div>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ProjectsDialog open={projectsOpen} onOpenChange={setProjectsOpen} />
    </header>
  );
}
